import React, { useState, useEffect } from "react";
import axios from "axios";
//import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./baseStyles.css";
import "./ListManagerPage.css";
import { Link, useNavigate } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

// Restored for myOrganizer Quick Add URL display
const quickAddBaseUrl =
  window.location.hostname === "localhost"
    ? "http://localhost:3000"
    : window.location.origin;


const ListManagerPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cognitoUserId, setCognitoUserId] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);


  const allowedNowUsers = [
    "24c8d4f8-e0d1-700b-91fe-340cedc4d1fb",
    "dummy"
  ];


  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  const parseApi = (res) =>
    (res?.data?.message && res.data) ||
    (typeof res?.data?.body === "string" ? JSON.parse(res.data.body) : res?.data || {});


  const [form, setForm] = useState({
    campaignName: "",
    campaignListCommMethod: "email",
    campaignListNextSeq: 1,
    campaignListReminderSchedule: "daily_8am",
    campaignListReminderDays: "Fri",

    // myOrganizer Quick Add
    myOrganizerCaptureToken: "",

    // myOrganizer AI Review
    myOrganizerAIEnabled: false,
    myOrganizerAIConfidenceThreshold: 85,
    myOrganizerAIGeneralInstructions: "",
    myOrganizerAISpecialtyInstructions: "",
  });


  const fetchInvites = async (userid) => {
    setInvitesLoading(true);
    try {
      const idToken = localStorage.getItem("idToken");
      const res = await axios.post(
        `${apiUrl}`,
        { operation: "list_my_invites", userid },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const payload = parseApi(res);
      setInvites(Array.isArray(payload.invites) ? payload.invites : []);
    } catch (err) {
      console.error("Fetch invites failed:", err);
      // non-blocking; don't surface as page error
    } finally {
      setInvitesLoading(false);
    }
  };


  useEffect(() => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) return navigate("/login");

    try {
      const decoded = jwtDecode(idToken);
      if (decoded.exp * 1000 < Date.now()) throw new Error("Token expired");
      setCognitoUserId(decoded.sub);
      fetchAllCampaigns(decoded.sub);
      fetchInvites(decoded.sub);
    } catch (e) {
      localStorage.removeItem("idToken");
      navigate("/login");
    }
  }, []);


  const acceptInvite = async (inviteId) => {
    try {
      const idToken = localStorage.getItem("idToken");
      const res = await axios.post(
        `${apiUrl}`,
        { operation: "accept_invite", userid: cognitoUserId, inviteId },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const payload = parseApi(res);
      setSuccessMessage(payload.message || "Invite accepted.");
      setTimeout(() => setSuccessMessage(null), 4000);

      // remove from local list + refresh campaigns
      setInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
      fetchAllCampaigns(cognitoUserId);
    } catch (err) {
      console.error("Accept invite failed:", err);
      const msg =
        err?.response?.data?.error ||
        (typeof err?.response?.data?.body === "string" &&
          JSON.parse(err.response.data.body).error) ||
        "Failed to accept invite.";
      setError(msg);
      setTimeout(() => setError(null), 5000);
    }
  };


  const declineInvite = async (inviteId) => {
    try {
      const idToken = localStorage.getItem("idToken");
      const res = await axios.post(
        `${apiUrl}`,
        { operation: "decline_invite", userid: cognitoUserId, inviteId },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const payload = parseApi(res);
      setSuccessMessage(payload.message || "Invite declined.");
      setTimeout(() => setSuccessMessage(null), 4000);

      setInvites((prev) => prev.filter((i) => i.inviteId !== inviteId));
    } catch (err) {
      console.error("Decline invite failed:", err);
      const msg =
        err?.response?.data?.error ||
        (typeof err?.response?.data?.body === "string" &&
          JSON.parse(err.response.data.body).error) ||
        "Failed to decline invite.";
      setError(msg);
      setTimeout(() => setError(null), 5000);
    }
  };


  // --- Invite helpers ---
  // Clean BigQuestion invite/list names safely.
  // - Always returns a string
  // - Strips the leading UUID/prefix up to first underscore
  // - Strips "bigquestion_" (case-insensitive) if present
  const cleanCampaignName = (raw) => {
    const s = (raw ?? "").toString();
    const afterFirstUnderscore = s.includes("_") ? s.slice(s.indexOf("_") + 1) : s;
    return afterFirstUnderscore.replace(/^bigquestion_/i, "");
  };

  // Short id fallback for "From User xxxxxxxx"
  const shortId = (id = "") => id.slice(0, 8);


  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const end = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff; // may be negative if expired
  };


  // Helper
  const renderCampaignName = (raw) => {
    if (!raw) return null;
    let name = String(raw);

    // (optional) strip one leading prefix before the first underscore
    name = name.replace(
      /^(?:bigquestion|favorites|personalreminders|commercialreminders|[A-Za-z0-9.-]+)_/,
      ""
    );

    // split ONLY at the "_listOfQuestions" boundary (case-insensitive)
    const sep = /_listofquestions\b/i;
    const sepIndex = name.search(sep);

    const head = sepIndex >= 0 ? name.slice(0, sepIndex) : name;
    const tail = sepIndex >= 0 ? name.slice(sepIndex + 1) : "";

    // show underscores but allow wrap *after* them
    const renderOneLine = (s) => {
      const parts = s.split("_");
      return parts.map((p, i) => (
        <React.Fragment key={i}>
          {p}
          {i < parts.length - 1 ? <>_<wbr /></> : null}
        </React.Fragment>
      ));
    };

    return (
      <>
        <div>{renderOneLine(head)}</div>
        {tail && <div>{tail}</div>}
      </>
    );
  };


  const fetchAllCampaigns = async (userid) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiUrl}`,
        {
          params: { operation: "fetch_all", userid },
          headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
        }
      );
      setCampaigns(Array.isArray(res.data) ? res.data : [res.data]);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        navigate("/login");
      } else {
        setError("Failed to load campaigns");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleEdit = (index) => {
    const item = campaigns[index];

    // Prevent edit for Favorites, BigQuestion, or personal reminder lists ending in _listOfQuestions
    const isUneditable =
      ["Favorites", "BigQuestion"].includes(item.campaignListCampaignType) ||
      (item.campaignListCampaignType === "PersonalReminders" &&
        item.campaignName.endsWith("_listOfQuestions"));

    if (isUneditable) return;

    const editable = {
      campaignName: item.campaignName,
      campaignListCommMethod: item.campaignListCommMethod,
      campaignListNextSeq: item.campaignListNextSeq,
      campaignListReminderSchedule: item.campaignListReminderSchedule || "",
      campaignListReminderDays: item.campaignListReminderDays || "Fri",
      campaignListReminderTime: item.campaignListReminderTime || "0",

      // Restored: existing Quick Add token from Organizer list record
      myOrganizerCaptureToken: item.myOrganizerCaptureToken || "",

      // myOrganizer AI Review
      myOrganizerAIEnabled: item.myOrganizerAIEnabled ?? false,
      myOrganizerAIConfidenceThreshold: item.myOrganizerAIConfidenceThreshold ?? 85,
      myOrganizerAIGeneralInstructions: item.myOrganizerAIGeneralInstructions || "",
      myOrganizerAISpecialtyInstructions: item.myOrganizerAISpecialtyInstructions || ""
    };

    setEditIndex(index);
    setForm(editable);
    setEditing(true);
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : ["campaignListNextSeq", "myOrganizerAIConfidenceThreshold"].includes(name)
          ? parseInt(value, 10) || 0
          : value,
    }));
  };


  const handleSubmit = async () => {
    const item = campaigns[editIndex];
    if (!cognitoUserId || !item) return;

    try {
      const isOrganizer =
        item.campaignName === "Organizer" ||
        item.campaignName.endsWith("_Organizer");

      await axios.post(
        `${apiUrl}`,
        {
          operation: "update_list_item",
          userid: cognitoUserId,
          campaignName: item.campaignName,
          campaignListCommMethod: form.campaignListCommMethod,
          campaignListNextSeq: form.campaignListNextSeq,
          campaignListReminderSchedule: form.campaignListReminderSchedule,
          campaignListReminderDays: form.campaignListReminderDays,
          campaignListReminderTime: form.campaignListReminderTime,

          // NOTE:
          // myOrganizerCaptureToken is intentionally NOT submitted.
          // Edit List only reads/displays the existing permanent token.
          ...(isOrganizer && {
            myOrganizerAIEnabled: form.myOrganizerAIEnabled,
            myOrganizerAIConfidenceThreshold: form.myOrganizerAIConfidenceThreshold,
            myOrganizerAIGeneralInstructions: form.myOrganizerAIGeneralInstructions,
            myOrganizerAISpecialtyInstructions: form.myOrganizerAISpecialtyInstructions
          })
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        }
      );

      fetchAllCampaigns(cognitoUserId);
      setEditing(false);
    } catch (err) {
      console.error("Update failed", err);

      if (err.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        navigate("/login");
      } else {
        setError(err.response?.data?.error || "Update failed");
      }
    }
  };


  const confirmAndRemove = async () => {
    if (!confirmDeleteItem || !cognitoUserId) return;

    try {
      const item = confirmDeleteItem;
      const type = item.campaignListCampaignType;

      const op = type === "Favorites"
        ? "remove_favorites_campaign"
        : type === "BigQuestion"
        ? "remove_big_question_campaign"
        : "delete_list_item";

      const res = await axios.post(
        `${apiUrl}`,
        {
          operation: op,
          userid: cognitoUserId,
          campaignName: item.campaignName
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`
          }
        }
      );

      // Show success message if present
      if (res.data?.message) {
        setSuccessMessage(res.data.message);
        setTimeout(() => setSuccessMessage(null), 4000);
      }

      fetchAllCampaigns(cognitoUserId);
    } catch (err) {
      console.error("Delete failed", err);

      if (err.response?.data?.message === "The incoming token has expired") {
        localStorage.removeItem("idToken");
        navigate("/login");
      } else {
        setError(err.response?.data?.error || "Delete failed");
      }
    } finally {
      setConfirmDeleteItem(null);
    }
  };


  /*
  insert new code
  */
  // --- Sorting & Label helpers (Commercial always last) ---
  const TYPE_LABEL = {
    Favorites: "Favorites",
    BigQuestion: "The Big Question",
    PersonalReminders: "Personal Reminders",
    CommercialReminders: "Commercial Reminders",
  };


  const getType = (item) =>
    item?.campaignListCampaignType || "PersonalReminders";


  const getTypeWeight = (type) => {
    if (type === "CommercialReminders") {
      return Number.MAX_SAFE_INTEGER;
    }

    const base = {
      Favorites: 0,
      BigQuestion: 1,
      PersonalReminders: 2
    };

    return base[type] ?? 50;
  };


  const sortCampaigns = (list) =>
    [...list].sort((a, b) => {
      const wa = getTypeWeight(getType(a));
      const wb = getTypeWeight(getType(b));

      if (wa !== wb) return wa - wb;

      const an = (a?.campaignName ?? "").toString();
      const bn = (b?.campaignName ?? "").toString();

      return an.localeCompare(bn);
    });


  /*
  end
  insert new code
  */

  /*
  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  */


  return (
    <div className="container list-manager-page">
      <h2 className="section-header">
        Manage your Lists and Programs
      </h2>

      {successMessage && (
        <p className="success-message">
          {successMessage}
        </p>
      )}

      {error && (
        <p className="error-message">
          {error}
        </p>
      )}


      {campaigns.length === 0 && !loading && (
        <div className="no-campaigns-message">
          <p>You haven’t signed up for anything yet.</p>

          <p>
            👉 Visit the{" "}
            <Link to="/campaign-catalog">
              <strong>Catalog</strong>
            </Link>{" "}
            to get started!
          </p>
        </div>
      )}


      <div className="centered-wrapper">

        {/* Pending Invites */}
        {!invitesLoading && invites.length > 0 && (
          <div className="lm-invites">
            <h3 className="section-subheader">
              You’ve been invited
            </h3>

            {invites.map((inv) => {
              const prettyName = cleanCampaignName(inv?.campaignName);

              const fromLabel =
                inv.inviterFirstName ||
                inv.inviterLoginId ||
                `User ${shortId(inv.inviterUserId || "")}`;

              return (
                <div
                  key={inv.inviteId}
                  className="lm-invite-card"
                >
                  <div className="lm-invite-head">
                    <div className="lm-invite-title">
                      {prettyName}
                    </div>

                    <div className="lm-invite-subtitle">
                      {fromLabel} invited you to join this private group.
                    </div>
                  </div>

                  <div className="lm-invite-body">
                    <p>
                      Answer the Big Question, see what everyone else thinks,
                      and compare answers when the results come out.
                    </p>
                  </div>

                  <div className="lm-invite-actions">
                    <button
                      className="remove-link"
                      onClick={() => declineInvite(inv.inviteId)}
                    >
                      Decline
                    </button>

                    <button
                      className="go-button"
                      onClick={() => acceptInvite(inv.inviteId)}
                    >
                      Accept
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}


        {/* start response-list */}
        <div className="response-list">
          {(() => {
            // Group by type after sorting (Commercial last) and skip empties
            const groups = {};

            sortCampaigns(campaigns).forEach((it) => {
              const t =
                it?.campaignListCampaignType ?? "PersonalReminders";

              (groups[t] ||= []).push(it);
            });

            return Object.entries(groups)
              .filter(([, arr]) => arr.length)
              .map(([type, rows]) => (
                <React.Fragment key={type}>
                  <h3 className="section-subheader">
                    {TYPE_LABEL[type] || type}
                  </h3>

                  {rows.map((row, index) => {
                    const rawName =
                      (row?.campaignName ?? "").toString();

                    const cleanedName =
                      rawName.replace(
                        new RegExp(`^${cognitoUserId}_`),
                        ""
                      );

                    const sendWhen =
                      row?.campaignListSendWhen ?? "";

                    const showSchedule =
                      sendWhen === "readNext" &&
                      [
                        "PersonalReminders",
                        "CommercialReminders",
                        "SystemTool",
                        "UserList"
                      ].includes(row?.campaignListCampaignType);

                    const scheduleLabel = showSchedule
                      ? `${row?.campaignListReminderSchedule || ""} (${row?.campaignListReminderDays || "Fri"})`
                      : sendWhen;

                    const isUneditable =
                      ["Favorites", "BigQuestion"].includes(
                        row?.campaignListCampaignType
                      ) ||
                      (
                        row?.campaignListCampaignType ===
                          "PersonalReminders" &&
                        rawName.endsWith("_listOfQuestions")
                      );

                    return (
                      <div
                        key={`${type}-${index}`}
                        className="campaign-card"
                      >
                        <div className="campaign-name">
                          {renderCampaignName(cleanedName)}
                        </div>

                        {/*
                        <div className="campaign-field">
                          Type: {row?.campaignListCampaignType}
                        </div>

                        <div className="campaign-field">
                          Comm Method: {row?.campaignListCommMethod}
                        </div>

                        <div className="campaign-field">
                          Next Seq: {row?.campaignListNextSeq}
                        </div>

                        <div className="campaign-field">
                          Send When: {scheduleLabel}
                        </div>
                        */}

                        <div className="campaign-buttons">
                          <button
                            className="remove-link"
                            onClick={() =>
                              setConfirmDeleteItem(row)
                            }
                          >
                            Remove
                          </button>

                          {!isUneditable && (
                            <button
                              className="remove-link edit-link"
                              onClick={() =>
                                handleEdit(
                                  campaigns.findIndex(
                                    (c) =>
                                      (c?.campaignName ?? "") ===
                                      rawName
                                  )
                                )
                              }
                            >
                              Edit
                            </button>
                          )}

                          <button
                            className="go-button"
                            onClick={() => {
                              let dest = "";

                              if (
                                row?.campaignListCampaignType ===
                                "Favorites"
                              ) {
                                dest =
                                  `/myfavorites?campaignName=${encodeURIComponent(rawName)}`;
                              } else if (
                                [
                                  "PersonalReminders",
                                  "CommercialReminders",
                                  "SystemTool",
                                  "UserList"
                                ].includes(
                                  row?.campaignListCampaignType
                                )
                              ) {
                                dest =
                                  `/item-manager/${encodeURIComponent(rawName)}`;
                              } else if (
                                row?.campaignListCampaignType ===
                                "BigQuestion"
                              ) {
                                dest =
                                  `/big-question/${rawName}`;
                              } else {
                                dest =
                                  `/campaign/${encodeURIComponent(rawName)}`;
                              }

                              navigate(dest);
                            }}
                          >
                            Go
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ));
          })()}
        </div>
        {/* end response-list */}

      </div>


      {/* Delete Confirm Modal */}
      {confirmDeleteItem && (
        <div className="modal">
          <div className="modal-content">
            <p>
              Are you sure you want to delete{" "}
              <strong>
                {confirmDeleteItem.campaignName.replace(
                  new RegExp(`^${cognitoUserId}_`),
                  ""
                )}
              </strong>
              ?
            </p>

            <div className="button-group">
              <button
                className="save-button"
                onClick={confirmAndRemove}
              >
                Yes, Delete
              </button>

              <button
                className="save-button"
                onClick={() =>
                  setConfirmDeleteItem(null)
                }
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Edit Modal */}
      {editing && (
        <div className="modal">
          <div
            className={`modal-content ${
              form.campaignName === "Organizer" ||
              form.campaignName.endsWith("_Organizer")
                ? "organizer-edit-modal"
                : ""
            }`}
          >
            <div className="edit-options-container">

              <h3>Edit Campaign Settings</h3>

              <label>
                Campaign Name:
                <input
                  type="text"
                  name="campaignName"
                  value={form.campaignName}
                  readOnly
                />
              </label>


              <label>
                Comm Method:
                <select
                  name="campaignListCommMethod"
                  value={form.campaignListCommMethod}
                  onChange={handleChange}
                >
                  <option value="email">
                    Email
                  </option>

                  <option value="text">
                    Text (not yet available)
                  </option>

                  <option value="other">
                    Other
                  </option>
                </select>
              </label>


              <label>
                Next Sequence:
                <input
                  type="number"
                  name="campaignListNextSeq"
                  value={form.campaignListNextSeq}
                  onChange={handleChange}
                />
              </label>


              <label>
                Reminder Schedule:
                <select
                  name="campaignListReminderSchedule"
                  value={form.campaignListReminderSchedule}
                  onChange={handleChange}
                >
                  {/* Only show "Now" option for allowed users */}
                  {allowedNowUsers.includes(cognitoUserId) && (
                    <option value="now">
                      Now
                    </option>
                  )}

                  <option value="daily">
                    Daily
                  </option>

                  <option value="weekly">
                    Weekly
                  </option>

                  <option value="none">
                    Skip / Inactive
                  </option>
                </select>
              </label>


              {form.campaignListReminderSchedule === "weekly" && (
                <label>
                  Reminder Day:

                  <select
                    name="campaignListReminderDays"
                    value={form.campaignListReminderDays}
                    onChange={handleChange}
                  >
                    <option value="Mon">Monday</option>
                    <option value="Tue">Tuesday</option>
                    <option value="Wed">Wednesday</option>
                    <option value="Thu">Thursday</option>
                    <option value="Fri">Friday</option>
                    <option value="Sat">Saturday</option>
                    <option value="Sun">Sunday</option>
                  </select>
                </label>
              )}


              <label>
                Reminder Time:

                <select
                  name="campaignListReminderTime"
                  value={form.campaignListReminderTime}
                  onChange={handleChange}
                >
                  <option value="">
                    -- Select Time --
                  </option>

                  {Array.from({ length: 17 }, (_, i) => {
                    const hour = i + 5;

                    const label =
                      `${hour % 12 === 0 ? 12 : hour % 12}:00 ${
                        hour < 12 ? "AM" : "PM"
                      }`;

                    const value =
                      `${hour.toString().padStart(2, "0")}:00`;

                    return (
                      <option
                        key={value}
                        value={value}
                      >
                        {label} (Central)
                      </option>
                    );
                  })}
                </select>
              </label>


              {(form.campaignName === "Organizer" ||
                form.campaignName.endsWith("_Organizer")) && (
                <>

                  {/* ===================================== */}
                  {/* myOrganizer Quick Add - RESTORED     */}
                  {/* ===================================== */}

                  <div className="myorganizer-quick-add">
                    <h3>myOrganizer Quick Add</h3>

                    <label>
                      Quick Add URL:

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          value={
                            form.myOrganizerCaptureToken
                              ? `${quickAddBaseUrl}/q/${form.myOrganizerCaptureToken}`
                              : ""
                          }
                          readOnly
                          style={{ flex: 1 }}
                        />

                        <button
                          type="button"
                          className="save-button"
                          onClick={() =>
                            navigator.clipboard.writeText(
                              form.myOrganizerCaptureToken
                                ? `${quickAddBaseUrl}/q/${form.myOrganizerCaptureToken}`
                                : ""
                            )
                          }
                          disabled={!form.myOrganizerCaptureToken}
                        >
                          Copy
                        </button>
                      </div>
                    </label>
                  </div>


                  {/* ===================================== */}
                  {/* Existing myOrganizer AI Review       */}
                  {/* ===================================== */}

                  <div className="myorganizer-ai-config">
                    <h3>myOrganizer AI Review</h3>

                    <div className="myorganizer-ai-top-row">

                      <label className="myorganizer-ai-enabled">
                        <input
                          type="checkbox"
                          name="myOrganizerAIEnabled"
                          checked={form.myOrganizerAIEnabled}
                          onChange={handleChange}
                        />

                        <span>
                          AI Review Enabled
                        </span>
                      </label>


                      <label className="myorganizer-ai-confidence">
                        <span>
                          Minimum Confidence:
                        </span>

                        <input
                          type="number"
                          name="myOrganizerAIConfidenceThreshold"
                          min="0"
                          max="100"
                          value={
                            form.myOrganizerAIConfidenceThreshold
                          }
                          onChange={handleChange}
                        />

                        <span>%</span>
                      </label>

                    </div>


                    <label className="myorganizer-ai-text-label">
                      General AI Instructions:

                      <textarea
                        name="myOrganizerAIGeneralInstructions"
                        rows="7"
                        value={
                          form.myOrganizerAIGeneralInstructions
                        }
                        onChange={handleChange}
                      />
                    </label>


                    <label className="myorganizer-ai-text-label">
                      Specialty AI Instructions:

                      <textarea
                        name="myOrganizerAISpecialtyInstructions"
                        rows="7"
                        value={
                          form.myOrganizerAISpecialtyInstructions
                        }
                        onChange={handleChange}
                      />
                    </label>

                  </div>
                </>
              )}


              <div className="button-group">

                <button
                  className="save-button"
                  onClick={handleSubmit}
                >
                  Save Changes
                </button>

                <button
                  className="save-button"
                  onClick={() =>
                    setEditing(false)
                  }
                >
                  Cancel
                </button>

              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};


export default ListManagerPage;